import { DateFormatHelper } from "@/Shared/Helpers/date-format.helper";
import { AppEnum } from "@/Shared/Types";
import { PersonModel } from "../PersonModel";
import { PersonFormValues } from "../PersonFormValidation";
import { formatName } from "@/Shared";


/**
 * Class: PersonFormViewModel
 *
 * Description:
 * ViewModel for managing person form data and options.
 * Provides form values, dropdown options, and utility methods
 * for working with PersonModel instances.
 * Designed to be extended by other form view models (e.g., ContactFormViewModel).
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class PersonFormViewModel {

    public firstname!: string;
    public lastname!: string;
    public fullName!: string;


    /**
     * =====================================================
     * Constructor
     * =====================================================
     *
     * Initializes form fields from an optional PersonModel.
     *
     * @param civilityEnum - Enum definitions for dropdowns
     * @param person - Optional PersonModel for editing
     */
    public constructor(
        protected civilityEnum : AppEnum[], 
        protected person?: PersonModel) {

        this.lastname = this.person?.lastname?.toUpperCase() ?? "";
        this.firstname = this.person?.firstname ?? "";
        this.fullName = formatName(this.person);
    }

    get id() {
        return this.person?.id;
    }


    /**
     * =====================================================
     * Form Values
     * =====================================================
     *
     * Provides values to populate the form fields, including
     * person info, birth date, address, contact details, and positions.
     */
    getFormValues() {
        return {
            id : this.id,
            lastname: this.lastname,
            firstname: this.firstname,
            civility: this.person?.civility || "",
            profession: this.person?.profession || "",
            birthDate: DateFormatHelper.toInput(this.person?.birthDate),
            address: this.person?.address || "",
            city: this.person?.city || "",
            country: this.person?.country || "",
            phone1: this.person?.phone1 || "",
            phone2: this.person?.phone2 || "",
            email: this.person?.email || ""
        } as PersonFormValues;
    }

    /**
     * =====================================================
     * Phone Utilities
     * =====================================================
     */
     hasPhone = () => {
        const phone1 = this.person?.phone1;
        const phone2 = this.person?.phone2;
        return !!(phone1 || phone2);
    }

    get eitherPhone() {
        const phone1 = this.person?.phone1;
        const phone2 = this.person?.phone2;
        return phone1 || phone2 || "";
    }

    /**
     * =====================================================
     * Mode Check
     * =====================================================
     *
     * Returns true if creating a new person (no ID set)
     */
    isCreationMode = () => !this?.id;


    get civilityOptions(): AppEnum[] {
        return this.civilityEnum;
    }

     /**
     * =====================================================
     * Title
     * =====================================================
     *
     * Returns a dynamic title based on whether editing or creating.
     *
     * @param personType - Optional type label for creation title
     */
    getTitle(personType? : string) {
        return this.person 
            ? this.fullName 
            : `${personType ?? 'Personne'} : Création`;
    }
}